import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { ThemeProvider } from "@mui/material/styles";
import { DEFAULT_BRANDING_CONFIG } from "../constants/defaultBranding";
import type {
  BrandingConfig,
  BrandingContextValue,
  GlobalBrandingChangeMessage,
} from "../types";
import {
  clearBrandingConfigCache,
  readBrandingConfigCache,
  writeBrandingConfigCache,
} from "../utils/cache";
import { applyCSSVariables } from "../utils/cssVariables";
import {
  mergeBrandingConfig,
  normalizeBrandingConfig,
} from "../utils/normalizeBranding";
import { createBrandingTheme } from "../utils/theme";
import {
  brandingDebug,
  brandingDebugEmbeddedBanner,
} from "../utils/brandingDebug";
import { parseGlobalBrandingMessage } from "../utils/parseGlobalBrandingMessage";
import { updateFavicon, updateTitle } from "../utils/updateMeta";
import { envConfig } from "../../config/env";
import { BrandingContext } from "./BrandingContextValue";

declare global {
  interface Window {
    __GRI_BRANDING_CONFIG__?: Partial<BrandingConfig>;
  }
}

interface BrandingProviderProps {
  children: React.ReactNode;
}

interface BrandingState {
  config: BrandingConfig;
  isLoading: boolean;
  error: Error | null;
}

function getInitialBrandingState(): BrandingState {
  const mockConfig =
    typeof window !== "undefined" ? window.__GRI_BRANDING_CONFIG__ : undefined;
  if (mockConfig) {
    brandingDebug("initial branding source", {
      source: "window.__GRI_BRANDING_CONFIG__",
      themeAndColors: normalizeBrandingConfig(mockConfig).themeAndColors,
    });
    return {
      config: normalizeBrandingConfig(mockConfig),
      isLoading: false,
      error: null,
    };
  }

  const cachedConfig =
    typeof window !== "undefined" ? readBrandingConfigCache() : null;

  if (cachedConfig) {
    brandingDebug("initial branding source", {
      source: "localStorage cache",
      themeAndColors: cachedConfig.themeAndColors,
    });
  } else {
    brandingDebug("initial branding source", {
      source: "DEFAULT_BRANDING_CONFIG",
      themeAndColors: DEFAULT_BRANDING_CONFIG.themeAndColors,
    });
  }

  return {
    config: cachedConfig ?? DEFAULT_BRANDING_CONFIG,
    isLoading: false,
    error: null,
  };
}

function getOrigin(url: string): string | null {
  try {
    return new URL(url).origin;
  } catch {
    return null;
  }
}

function getAllowedMessageOrigins(): Set<string> {
  const origins = new Set<string>();

  if (typeof window === "undefined") return origins;

  origins.add(window.location.origin);

  const platformOrigin = getOrigin(envConfig.platformBaseUrl);
  if (platformOrigin) origins.add(platformOrigin);

  if (document.referrer) {
    const referrerOrigin = getOrigin(document.referrer);
    if (referrerOrigin) origins.add(referrerOrigin);
  }

  return origins;
}

function isGlobalBrandingChangeMessage(
  data: unknown,
): data is GlobalBrandingChangeMessage {
  return (
    Boolean(data) &&
    typeof data === "object" &&
    ((data as GlobalBrandingChangeMessage).type === "globalBrandingChange" ||
      (data as GlobalBrandingChangeMessage).type === "globalBrandingChang")
  );
}

export function BrandingProvider({ children }: Readonly<BrandingProviderProps>) {
  const [state, setState] = useState<BrandingState>(getInitialBrandingState);

  useEffect(() => {
    brandingDebugEmbeddedBanner();
  }, []);

  const applyConfig = useCallback(
    (config: Partial<BrandingConfig>, shouldCache = true) => {
      setState((current) => {
        const nextConfig = mergeBrandingConfig(current.config, config);
        brandingDebug("applyConfig (branding updated)", {
          shouldCache,
          previousPrimary: current.config.themeAndColors.primaryColor,
          nextPrimary: nextConfig.themeAndColors.primaryColor,
          nextThemeAndColors: nextConfig.themeAndColors,
        });
        if (shouldCache) writeBrandingConfigCache(nextConfig);
        return {
          config: nextConfig,
          isLoading: false,
          error: null,
        };
      });
    },
    [],
  );

  useEffect(() => {
    brandingDebug("BrandingProvider active config", {
      themeAndColors: state.config.themeAndColors,
      title: state.config.brandIdentity.title,
    });
  }, [state.config]);

  useEffect(() => {
    applyCSSVariables(state.config.themeAndColors);
    updateTitle(state.config.extra?.pageTitle ?? state.config.brandIdentity.title);
    updateFavicon(state.config.brandIdentity.favicon);
  }, [state.config]);

  useEffect(() => {
    const allowedOrigins = getAllowedMessageOrigins();

    const handleMessage = (event: MessageEvent) => {
      const looksLikeBranding = isGlobalBrandingChangeMessage(event.data);

      if (!allowedOrigins.has(event.origin)) {
        if (looksLikeBranding) {
          brandingDebug("postMessage ignored (origin not allowed)", {
            origin: event.origin,
            allowedOrigins: [...allowedOrigins],
          });
        }
        return;
      }
      if (!looksLikeBranding) return;

      const parsed = parseGlobalBrandingMessage(event.data);
      if (!parsed.ok) {
        brandingDebug("postMessage globalBrandingChange parse failed", {
          origin: event.origin,
          reason: parsed.reason,
          rawData: event.data.data,
          rawPayload: event.data.payload,
        });
        return;
      }

      brandingDebug("postMessage globalBrandingChange received", {
        origin: event.origin,
        source: parsed.source,
        themeAndColors: parsed.config.themeAndColors,
      });

      applyConfig(parsed.config);
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [applyConfig]);

  const theme = useMemo(
    () => createBrandingTheme(state.config),
    [state.config],
  );

  const value = useMemo<BrandingContextValue>(
    () => ({
      config: state.config,
      isLoading: state.isLoading,
      error: state.error,
      updateConfig: applyConfig,
      clearCache: clearBrandingConfigCache,
    }),
    [applyConfig, state.config, state.error, state.isLoading],
  );

  return (
    <BrandingContext.Provider value={value}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </BrandingContext.Provider>
  );
}

